<template>
  <AppModal
    :show="isVisible"
    :title="defaultTitle"
    :size="'medium'"
    :gradientHeader="false"
    :loading="loading"
    :closeOnBackdropClick="closeOnOverlayClick"
    @close="handleCancel"
  >
    <!-- Main content -->
    <div class="confirm-modal__content">
      <!-- Icon and message -->
      <div class="confirm-modal__message-section">
        <div class="confirm-modal__icon" :class="`confirm-modal__icon--${type}`">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path v-if="type === 'warning'" fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
            <path v-else-if="type === 'danger'" fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
            <path v-else-if="type === 'success'" fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
            <path v-else fill="currentColor" d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
          </svg>
        </div>
        <div class="confirm-modal__text">
          <p class="confirm-modal__message">{{ message }}</p>
        </div>
      </div>
      
      <!-- Details section (optional) -->
      <div v-if="details" class="confirm-modal__details" :class="`confirm-modal__details--${type}`">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
        </svg>
        <div class="confirm-modal__details-text">
          {{ details }}
        </div>
      </div>
    </div>    <!-- Custom footer -->
    <template #footer>
      <div class="confirm-modal__actions">
        <AppButton 
          type="button"
          variant="outline" 
          @click="handleCancel"
          :disabled="loading"
          class="confirm-modal__btn confirm-modal__btn--cancel"
        >
          {{ defaultCancelText }}
        </AppButton>
        <AppButton 
          type="button"
          :variant="confirmButtonVariant"
          @click="handleConfirm"
          :disabled="loading"
          :loading="loading"
          class="confirm-modal__btn confirm-modal__btn--confirm"
        >
          {{ defaultConfirmText }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '../base/Modal.vue'
import AppButton from '../base/AppButton.vue'

// Define props interface
interface Props {
  isVisible?: boolean
  title?: string
  message: string
  details?: string
  confirmText?: string
  cancelText?: string
  type?: 'warning' | 'danger' | 'info' | 'success'
  loading?: boolean
  closeOnOverlayClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  title: '',
  details: '',
  confirmText: '',
  cancelText: '',
  type: 'warning',
  loading: false,
  closeOnOverlayClick: true
})

// Define emits interface
interface Emits {
  confirm: []
  cancel: []
  close: []
}

const emit = defineEmits<Emits>()

// i18n
const { t } = useI18n()

// Computed properties
const confirmButtonVariant = computed((): string => {
  const variants = {
    warning: 'warning',
    danger: 'danger',
    info: 'primary',
    success: 'success'
  }
  return variants[props.type as keyof typeof variants] || variants.warning
})

const defaultTitle = computed((): string => {
  if (props.title) return props.title
  const titles = {
    warning: t('common.confirm'),
    danger: t('common.delete'),
    info: t('common.info'),
    success: t('common.success')
  }
  return titles[props.type as keyof typeof titles] || titles.warning
})

const defaultConfirmText = computed((): string => {
  if (props.confirmText) return props.confirmText
  const texts = {
    warning: t('common.confirm'),
    danger: t('common.delete'),
    info: t('common.ok'),
    success: t('common.ok')
  }
  return texts[props.type as keyof typeof texts] || texts.warning
})

const defaultCancelText = computed((): string => {
  return props.cancelText || t('common.cancel')
})

// Methods
const handleConfirm = (): void => {
  emit('confirm')
}

const handleCancel = (): void => {
  emit('cancel')
  emit('close')
}

const handleOverlayClick = (): void => {
  if (props.closeOnOverlayClick && !props.loading) {
    handleCancel()
  }
}
</script>

<style scoped>
/* ==========================================================================
   CONFIRM MODAL STYLES
   ========================================================================== */

.confirm-modal__content {
  gap: var(--space-4);
}

.confirm-modal__message-section {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
}

.confirm-modal__icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.confirm-modal__icon--warning {
  background: var(--warning-50);
  color: var(--warning-600);
}

.confirm-modal__icon--danger {
  background: var(--error-50);
  color: var(--error-600);
}

.confirm-modal__icon--success {
  background: var(--success-50);
  color: var(--success-600);
}

.confirm-modal__icon--info {
  background: var(--info-50);
  color: var(--info-600);
}

.confirm-modal__text {
  flex: 1;
  min-width: 0;
}

.confirm-modal__message {
  font-size: var(--font-size-md);
  color: var(--text-color-secondary);
  margin: 0;
  line-height: 1.5;
}

.confirm-modal__details {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border-left: 4px solid;
  font-size: var(--text-sm);
  line-height: 1.5;
}

.confirm-modal__details--warning {
  background: var(--warning-50);
  border-left-color: var(--warning-400);
  color: var(--warning-800);
}

.confirm-modal__details--danger {
  background: var(--error-50);
  border-left-color: var(--error-400);
  color: var(--error-800);
}

.confirm-modal__details--success {
  background: var(--success-50);
  border-left-color: var(--success-400);
  color: var(--success-800);
}

.confirm-modal__details--info {
  background: var(--info-50);
  border-left-color: var(--info-400);
  color: var(--info-800);
}

.confirm-modal__details svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.confirm-modal__details-text {
  flex: 1;
  min-width: 0;
}

.confirm-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
}

.confirm-modal__btn {
  min-width: 100px;
}

/* Responsive design */
@media (max-width: 640px) {
  .confirm-modal__message-section {
    gap: var(--space-3);
  }
  
  .confirm-modal__icon {
    width: 40px;
    height: 40px;
  }
  
  .confirm-modal__message {
    font-size: var(--text-sm);
  }
  
  .confirm-modal__actions {
    flex-direction: column-reverse;
    gap: var(--space-2);
  }
  
  .confirm-modal__btn {
    width: 100%;
  }
}
</style>
